import * as constant from '@app/constants';
import * as models from '@app/models';
import { IPaginationToken } from '@app/models';
import { Injectable } from 'injection-js';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

const initialImageWidthOrHeight = 300;

@Injectable()
export class SharedService {
	public static moveElementsToStart = <T>(arr: T[], filter: (elem: T) => boolean) => {
		return _(arr).sortBy(filter).value();
	};

	public static filterAndMap = <T, M>(elements: T[], filter: (item: T) => boolean, map: (item: T) => M): M[] => {
		const result: M[] = [];

		elements.forEach((x) => {
			if (filter(x)) {
				result.push(map(x));
			}
		});

		return result;
	};

	public static isPasswordContainsUserOrAccountName(password: string, userDetails: models.IUserSignInResponse) {
		const accountName = userDetails.account.name?.toLowerCase();
		const firstName = userDetails.user.firstName?.toLowerCase();
		const lastName = userDetails.user.lastName?.toLowerCase();

		password = password.toLowerCase();
		if (password.includes(accountName) || password.includes(firstName) || password.includes(lastName)) {
			return true;
		}
		return false;
	}

	public static getEnumKeyByEnumValue(enumName: any, enumValue: string[]): string[] {
		const keys = enumValue.map((ele, index) => {
			return Object.keys(enumName).filter((x) => enumName[x] === ele)[0];
		});
		return keys;
	}

	constructor() {}
	/**
	 * Shows the file dialog to user, to select file.
	 * @param multiple
	 */
	public showFileDialog(
		multiple: boolean = false,
		acceptedFilesTypes: string = null,
		callback: (res: File | FileList, err?: any) => Promise<void>
	) {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = acceptedFilesTypes;
		input.style.display = 'none';
		input.multiple = multiple;
		input.click();
		input.onchange = () => {
			callback(input.files);
		};
	}

	/**
	 * Downloads a file using Blob
	 */
	public downloadFile(blob: Blob, name: string) {
		const a = document.createElement('a');
		// To handle download in firefox
		document.body.appendChild(a);
		a.style.display = 'none';
		const url = window.URL.createObjectURL(blob);
		a.href = url;
		a.download = name;
		a.click();
		document.body.removeChild(a);
	}

	/**
	 * parse content disposition
	 * @param content
	 */
	public parseContentDisposition(disposition: string): string {
		const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
		const matches = filenameRegex.exec(disposition);
		if (matches != null && matches[1]) {
			return matches[1].replace(/['"]/g, '');
		}
		return 'File';
	}

	/**
	 * parse error messages
	 * @param data
	 */
	public parseError(err: models.IErrorResponse, prependCode: boolean = false): string[] {
		const errorBody: models.IErrorBody[] = [
			{
				code: null,
				message: 'Server is unreachable',
			},
		];

		const formatError = (error: models.IErrorBody) =>
			!!error.code && prependCode ? `${error.code}: ${error.message}` : error.message;
		return !!err ? err.errors?.map((e) => formatError(e)) : errorBody.map((e) => formatError(e));
	}

	/**
	 * parse error code
	 * @param data
	 */
	public parseErrorCode(err: models.IErrorResponse): string[] {
		const formatError = (error: models.IErrorBody) => error.code;
		return err.errors.map((e) => formatError(e));
	}

	// Returns a random transaction id
	public createTransactionId(): string {
		return `${constant.transactionIdStarting}${uuidv4()}`;
	}

	/**
	 * Compress an image
	 */
	public compressImage(base64Url: string, width: number, height: number, callback: (url: string) => void) {
		const img = new Image();
		img.src = base64Url;
		img.onload = () => {
			const widthCheck = img.width < img.height;
			const imageAspect = widthCheck ? img.height / img.width : img.width / img.height;
			if (img.width > width || img.height > height) {
				const elem = document.createElement('canvas');
				elem.width = widthCheck ? initialImageWidthOrHeight : initialImageWidthOrHeight * imageAspect;
				elem.height = widthCheck ? initialImageWidthOrHeight * imageAspect : initialImageWidthOrHeight;
				const ctx = elem.getContext('2d');
				ctx.drawImage(img, 0, 0, elem.width, elem.height);
				callback(elem.toDataURL());
				return;
			}
			callback(base64Url);
			return;
		};
	}

	public getNewDateIfNotExist(date: Date): ReactIntl.DateSource {
		return date ?? new Date().toDateString();
	}

	public generatePaginationToken(currentPage: number) {
		const pageToken: IPaginationToken = {
			Current: currentPage,
		};

		return btoa(JSON.stringify(pageToken));
	}
}



// WEBPACK FOOTER //
// ./src/services/shared.service.ts